import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, ArrowUpRight, AlertTriangle, RotateCcw, Loader2,
  Scale, Info, Download, GitCompareArrows, X, CheckCircle2, Search, HelpCircle,
} from "lucide-react";
import { toast } from "sonner";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";
import { OPPORTUNITY_TITLES_LIST, RURAL_REVIEW_DATE } from "@/data/rural";

const Q = [
  { key: "lga", label: "Local government area", type: "text", placeholder: "e.g. Bega Valley, Wingecarribee…" },
  { key: "size", label: "Approximate property size", type: "select", options: ["Under 2 ha", "2–15 ha", "15–40 ha", "Over 40 ha"] },
  { key: "zoning", label: "Zoning, if known", type: "select", options: ["Unknown", "RU1 Primary Production", "RU2 Rural Landscape", "RU4 Primary Production Small Lots", "Other / rural-residential"] },
  { key: "farm", label: "The property is a…", type: "select", options: ["Commercial farm", "Hobby farm", "Vacant rural block", "Not sure"] },
  { key: "uses", label: "Current uses (select all that apply)", type: "multi", options: ["Grazing / livestock", "Cropping or horticulture", "Existing dwelling", "Farm buildings / sheds", "None yet"] },
  { key: "amenities", label: "Available on site", type: "multi", options: ["All-weather road access", "Power", "Water supply", "Toilet facilities", "Phone/internet reception"] },
  { key: "constraints", label: "Known constraints", type: "multi", options: ["Bushfire-prone land", "Flood-prone land", "Waterways / creeks", "Native vegetation / habitat", "None known"] },
  { key: "budget", label: "Approximate setup budget", type: "select", options: ["Under $2,000", "$2,000–$10,000", "$10,000–$50,000", "Over $50,000"] },
  { key: "involvement", label: "Preferred level of involvement", type: "select", options: ["Hands-off (lease it out)", "Part-time (weekends)", "Hands-on (a real side business)"] },
];

const STATUS_META = {
  potential: { label: "Worth investigating", cls: "bg-teal text-ivory" },
  investigate: { label: "Needs careful checks", cls: "bg-sand text-teal" },
  unlikely: { label: "May not fit", cls: "bg-cloud text-charcoal/70" },
};

const PATHWAY_META = {
  exempt: { title: "Exempt development", blurb: "No approval needed — if every published standard is met" },
  complying: { title: "Complying development", blurb: "Fast-tracked certificate — if every standard is met" },
  consent: { title: "Development consent (DA)", blurb: "Council assesses the proposal on its merits" },
  prohibited: { title: "Prohibited", blurb: "Not permitted in the zone — a different approach is needed" },
};

const ToolkitCta = () => {
  const [product, setProduct] = useState(null);
  const [buying, setBuying] = useState(false);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setProduct(data.find((p) => p.id === "rural-opportunities") || null);
    }).catch(() => {});
  }, []);

  const buy = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: ["rural-opportunities"],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout could not be started. Please try again.");
      setBuying(false);
    }
  };

  const onSale = product?.available;

  return (
    <div className="bg-teal text-ivory p-8 lg:p-10" data-testid="explorer-toolkit-cta">
      <Eyebrow dark>Take it further</Eyebrow>
      <h3 className="mt-3 font-serif text-2xl lg:text-3xl tracking-tight">
        Discover the possibilities. Understand the requirements. Plan your next move.
      </h3>
      <p className="mt-4 text-ivory/80 leading-relaxed max-w-2xl">
        Your land may hold opportunities you haven't considered. Our $19 Rural Land Opportunities
        Toolkit &amp; Workbook helps you explore your options, understand the right questions to ask
        and organise your research — property details, planning controls, official resources,
        opportunity shortlists and notes from council conversations — before speaking with council
        or a planning professional. Fillable on screen, printable for the kitchen table.
      </p>
      <div className="mt-7 flex flex-wrap items-center gap-4">
        {onSale ? (
          <button
            onClick={buy}
            disabled={buying}
            data-testid="explorer-buy-toolkit"
            className="inline-flex items-center gap-2 bg-ivory text-teal text-sm font-medium px-7 py-3.5 rounded-full hover:bg-sand transition-colors disabled:opacity-60"
          >
            {buying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Download the $19 Toolkit
          </button>
        ) : (
          <span
            className="inline-flex items-center gap-2 border border-ivory/40 text-ivory text-sm font-medium px-7 py-3.5 rounded-full"
            data-testid="explorer-toolkit-coming-soon"
          >
            $19 Toolkit — Coming Soon
          </span>
        )}
        <span className="text-xs text-ivory/60 font-mono uppercase tracking-[0.14em]">
          GST included · Instant PDF
        </span>
      </div>
    </div>
  );
};

const CompareTable = ({ items, onRemove, onClose }) => (
  <div className="fixed inset-0 z-50 bg-teal/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6" data-testid="compare-modal">
    <div className="bg-ivory w-full max-w-6xl max-h-[92vh] overflow-y-auto sm:rounded-sm">
      <div className="sticky top-0 bg-ivory border-b border-cloud px-6 py-4 flex items-center justify-between z-10">
        <p className="font-serif text-lg text-teal">Compare My Opportunities</p>
        <button onClick={onClose} data-testid="compare-close" className="p-2 text-charcoal/60 hover:text-teal">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="p-6 overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse">
          <thead>
            <tr>
              <th className="text-left align-top p-3 w-40" />
              {items.map((o) => (
                <th key={o.key} className="text-left align-top p-3 border-l border-cloud">
                  <p className="font-serif text-base text-teal">{o.title}</p>
                  <span className={`mt-1.5 inline-block text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-0.5 ${STATUS_META[o.status].cls}`}>
                    {STATUS_META[o.status].label}
                  </span>
                  <button onClick={() => onRemove(o.key)} className="mt-2 block text-[10px] font-mono uppercase tracking-[0.1em] text-charcoal/50 underline underline-offset-2 hover:text-teal">
                    Remove
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              ["Planning controls", (o) => o.controls],
              ["What to investigate", (o) => o.investigate],
              ["Infrastructure & site", (o) => o.infrastructure],
              ["Questions to ask", (o) => o.council_questions],
            ].map(([label, get]) => (
              <tr key={label} className="border-t border-cloud align-top">
                <td className="p-3 text-xs font-mono uppercase tracking-[0.12em] text-teal">{label}</td>
                {items.map((o) => (
                  <td key={o.key} className="p-3 border-l border-cloud text-xs text-charcoal/75 leading-relaxed">
                    {Array.isArray(get(o)) ? (
                      <ul className="space-y-1.5">
                        {get(o).map((t, i) => <li key={i} className="flex gap-1.5"><span className="mt-1.5 w-1 h-1 rounded-full bg-sand shrink-0" />{t}</li>)}
                      </ul>
                    ) : (
                      get(o)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

const OpportunityCard = ({ o, inCompare, compareFull, onToggleCompare }) => {
  const [open, setOpen] = useState(false);
  const sm = STATUS_META[o.status];
  return (
    <div className="bg-white border border-cloud" data-testid={`opportunity-${o.key}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left p-6" data-testid={`opportunity-toggle-${o.key}`}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-serif text-lg text-teal">{o.title}</p>
            <p className="mt-1.5 text-sm text-charcoal/70 leading-relaxed max-w-2xl">{o.what}</p>
          </div>
          <span className={`shrink-0 text-[10px] font-mono uppercase tracking-[0.1em] px-2.5 py-1 ${sm.cls}`}>
            {sm.label}
          </span>
        </div>
        <p className="mt-3 text-xs font-mono uppercase tracking-[0.14em] text-teal/70">
          {open ? "Hide detail −" : "Why it may fit, controls & checks +"}
        </p>
      </button>
      {open && (
        <div className="border-t border-cloud px-6 py-5 space-y-5">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">Why it may suit your property</p>
            <p className="mt-2 text-sm text-charcoal/80 leading-relaxed">{o.why_relevant}</p>
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">Planning controls that may apply</p>
            <p className="mt-2 text-sm text-charcoal/80 leading-relaxed">{o.controls}</p>
          </div>
          {[
            ["What to investigate", o.investigate],
            ["Infrastructure & site considerations", o.infrastructure],
            ["Questions to ask council or a planner", o.council_questions],
          ].map(([label, items]) =>
            items.length ? (
              <div key={label}>
                <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">{label}</p>
                <ul className="mt-2 space-y-1.5">
                  {items.map((t, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-charcoal/80 leading-relaxed">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sand shrink-0" />{t}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          )}
        </div>
      )}
      <div className="px-6 pb-5">
        <button
          onClick={() => onToggleCompare(o)}
          disabled={!inCompare && compareFull}
          data-testid={`opportunity-compare-${o.key}`}
          className={`inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.12em] px-4 py-2 border rounded-full transition-colors disabled:opacity-40 ${
            inCompare ? "bg-teal text-ivory border-teal" : "border-cloud text-charcoal/70 hover:border-teal hover:text-teal"
          }`}
        >
          <GitCompareArrows className="w-3.5 h-3.5" />
          {inCompare ? "Added to compare" : "Add to compare"}
        </button>
      </div>
    </div>
  );
};

const RuralExplorer = () => {
  const [answers, setAnswers] = useState({ uses: [], amenities: [], constraints: [], interests: [] });
  const [phase, setPhase] = useState("form"); // form | processing | ready | error
  const [result, setResult] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const pollRef = useRef(null);

  const set = (k, v) => setAnswers((s) => ({ ...s, [k]: v }));
  const toggle = (k, v) =>
    setAnswers((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const poll = (id) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/rural/roadmap/${id}`);
        if (data.status === "ready") {
          setResult(data.result);
          setPhase("ready");
        } else if (data.status === "error") {
          setPhase("error");
        } else {
          poll(id);
        }
      } catch {
        poll(id);
      }
    }, 3500);
  };

  const generate = async () => {
    setPhase("processing");
    try {
      const { data } = await api.post("/rural/roadmap", answers);
      poll(data.id);
    } catch {
      toast.error("Could not start your roadmap — please try again.");
      setPhase("form");
    }
  };

  const restart = () => {
    clearTimeout(pollRef.current);
    setResult(null);
    setCompare([]);
    setCompareOpen(false);
    setPhase("form");
  };

  const toggleCompare = (o) => {
    setCompare((prev) =>
      prev.find((x) => x.key === o.key)
        ? prev.filter((x) => x.key !== o.key)
        : prev.length >= 3
          ? prev
          : [...prev, o]
    );
  };

  return (
    <div data-testid="rural-explorer-page">
      <Seo
        title="Rural Land Opportunities Explorer"
        description="Describe your NSW rural property and goals — get a personalised educational roadmap of potential land uses, planning considerations, official resources and the questions worth asking."
      />
      <section className="bg-sand-light/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <Link to="/rural" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em] text-charcoal/60 hover:text-teal transition-colors">
            <ArrowLeft className="w-4 h-4" /> Rural Land
          </Link>
          <h1 className="mt-5 font-serif text-3xl sm:text-4xl text-teal tracking-tight">Rural Land Opportunities Explorer</h1>
          <p className="mt-3 text-charcoal/70 leading-relaxed max-w-2xl">
            Tell us about your property and what you'd love to achieve. You'll receive a personalised
            educational roadmap: potential opportunities, the planning considerations that apply,
            official resources, and the questions worth asking — all in plain English.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {phase === "form" && (
            <div className="space-y-8" data-testid="explorer-form">
              <FadeIn>
                <div className="bg-white border border-cloud p-6 space-y-5">
                  <p className="font-serif text-lg text-teal">About your property</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-charcoal">Property address or locality</p>
                      <input
                        value={answers.locality || ""}
                        onChange={(e) => set("locality", e.target.value)}
                        placeholder="e.g. 123 Range Road, Bega NSW"
                        data-testid="explorer-input-locality"
                        className="mt-2 w-full bg-ivory border border-cloud px-4 py-3 text-sm focus:outline-none focus:border-teal"
                      />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-charcoal">Lot &amp; DP <span className="text-charcoal/50 font-normal">(optional)</span></p>
                      <input
                        value={answers.lot_dp || ""}
                        onChange={(e) => set("lot_dp", e.target.value)}
                        placeholder="e.g. Lot 12 DP 883421"
                        data-testid="explorer-input-lotdp"
                        className="mt-2 w-full bg-ivory border border-cloud px-4 py-3 text-sm focus:outline-none focus:border-teal"
                      />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-charcoal">What would you love to achieve with your land?</p>
                    <textarea
                      value={answers.goals || ""}
                      onChange={(e) => set("goals", e.target.value)}
                      rows={3}
                      placeholder="e.g. We'd love to earn some income from our hobby farm — maybe weekend campers or selling our honey — but we have no idea what's allowed…"
                      data-testid="explorer-input-goals"
                      className="mt-2 w-full bg-ivory border border-cloud px-4 py-3 text-sm focus:outline-none focus:border-teal"
                    />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-charcoal">Which opportunities are you curious about? <span className="text-charcoal/50 font-normal">(select any)</span></p>
                    <div className="mt-2.5 flex flex-wrap gap-2">
                      {OPPORTUNITY_TITLES_LIST.map((t) => (
                        <button
                          key={t}
                          onClick={() => toggle("interests", t)}
                          data-testid={`explorer-interest-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                          className={`text-xs px-4 py-2.5 border rounded-full transition-colors ${
                            answers.interests.includes(t)
                              ? "bg-teal text-ivory border-teal"
                              : "bg-white border-cloud text-charcoal/70 hover:border-teal"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </FadeIn>

              {Q.map((q, i) => (
                <FadeIn key={q.key} delay={i * 0.02}>
                  <div>
                    <p className="text-sm font-medium text-charcoal">
                      <span className="font-mono text-sand mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {q.label}
                    </p>
                    {q.type === "text" && (
                      <input
                        value={answers[q.key] || ""}
                        onChange={(e) => set(q.key, e.target.value)}
                        placeholder={q.placeholder}
                        data-testid={`explorer-input-${q.key}`}
                        className="mt-2.5 w-full max-w-md bg-white border border-cloud px-4 py-3 text-sm focus:outline-none focus:border-teal"
                      />
                    )}
                    {(q.type === "select" || q.type === "multi") && (
                      <div className="mt-2.5 flex flex-wrap gap-2">
                        {q.options.map((o) => {
                          const active = q.type === "select" ? answers[q.key] === o : answers[q.key].includes(o);
                          return (
                            <button
                              key={o}
                              onClick={() => (q.type === "select" ? set(q.key, o) : toggle(q.key, o))}
                              data-testid={`explorer-option-${q.key}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              className={`text-xs px-4 py-2.5 border rounded-full transition-colors ${
                                active ? "bg-teal text-ivory border-teal" : "bg-white border-cloud text-charcoal/70 hover:border-teal"
                              }`}
                            >
                              {o}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </FadeIn>
              ))}

              <button
                onClick={generate}
                data-testid="explorer-generate"
                className="inline-flex items-center gap-2 bg-teal text-ivory text-sm font-medium px-7 py-3.5 rounded-full hover:bg-teal-dark transition-colors"
              >
                Build my opportunities roadmap <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-charcoal/55 leading-relaxed max-w-xl">
                Educational guidance only — generated with AI assistance. It is not planning or legal
                advice and never confirms that a use is permitted on your land.{" "}
                <Link to="/legal/ai-disclaimer" className="text-teal underline underline-offset-2">How our AI tools work</Link>
              </p>
            </div>
          )}

          {phase === "processing" && (
            <div className="py-20 text-center" data-testid="explorer-processing">
              <Loader2 className="w-8 h-8 text-teal animate-spin mx-auto" />
              <p className="mt-5 font-serif text-2xl text-teal">Exploring your property's potential…</p>
              <p className="mt-2 text-sm text-charcoal/60 max-w-md mx-auto">
                We're matching your answers against the NSW planning framework and official resources.
                This usually takes 30–60 seconds.
              </p>
            </div>
          )}

          {phase === "error" && (
            <div className="py-16 text-center" data-testid="explorer-error">
              <AlertTriangle className="w-8 h-8 text-sand mx-auto" />
              <p className="mt-4 font-serif text-2xl text-teal">Something didn't work</p>
              <p className="mt-2 text-sm text-charcoal/60">We couldn't prepare your roadmap this time.</p>
              <button onClick={restart} className="mt-6 inline-flex items-center gap-2 border border-teal text-teal text-sm px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors" data-testid="explorer-error-restart">
                <RotateCcw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {phase === "ready" && result && (
            <div className="space-y-10" data-testid="explorer-result">
              <FadeIn>
                <div className="bg-sand-light/60 border border-cloud p-6" data-testid="explorer-ai-notice">
                  <p className="font-serif text-lg text-teal">Your Educational Opportunities Roadmap</p>
                  <p className="mt-2 text-xs text-charcoal/75 leading-relaxed">
                    Generated using AI-assisted technology for educational and preliminary planning
                    purposes only. It is not professional planning or legal advice, and it does not
                    confirm that any use is permissible or approved on your property. Permissibility
                    depends on your zoning, the applicable LEP, NSW planning instruments and
                    site-specific constraints. Confirm requirements with your council, a registered
                    certifier or an appropriately qualified professional before acting.
                  </p>
                </div>
              </FadeIn>

              <FadeIn>
                <div className="bg-white border border-cloud p-7" data-testid="explorer-snapshot">
                  <Eyebrow>Your property snapshot{result.council_name ? ` — ${result.council_name}` : ""}</Eyebrow>
                  <div className="mt-5 grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">What you've told us</p>
                      <ul className="mt-2.5 space-y-2">
                        {result.property_snapshot.known.map((t, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-charcoal/80 leading-relaxed">
                            <CheckCircle2 className="w-4 h-4 text-teal shrink-0 mt-0.5" />{t}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-xs font-mono uppercase tracking-[0.14em] text-teal">What needs checking — and why</p>
                      <ul className="mt-2.5 space-y-2">
                        {result.property_snapshot.verify.map((t, i) => (
                          <li key={i} className="flex gap-2.5 text-sm text-charcoal/80 leading-relaxed">
                            <Search className="w-4 h-4 text-sand shrink-0 mt-0.5" />{t}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </FadeIn>

              <FadeIn>
                <div data-testid="explorer-pathways">
                  <Eyebrow>How approvals work in NSW</Eyebrow>
                  <p className="mt-4 text-sm text-charcoal/80 leading-relaxed max-w-3xl">{result.pathway_primer}</p>
                  <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-cloud border border-cloud">
                    {result.pathways.map((p) => (
                      <div key={p.key} className="bg-white p-5" data-testid={`pathway-${p.key}`}>
                        <Scale className="w-5 h-5 text-teal" />
                        <p className="mt-3 font-serif text-base text-teal">{PATHWAY_META[p.key]?.title}</p>
                        <p className="mt-1 text-[11px] font-mono uppercase tracking-[0.1em] text-charcoal/50">{PATHWAY_META[p.key]?.blurb}</p>
                        <p className="mt-2.5 text-xs text-charcoal/75 leading-relaxed">{p.relevance}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </FadeIn>

              <FadeIn>
                <div>
                  <Eyebrow>Potential opportunities to investigate</Eyebrow>
                  <p className="mt-3 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
                    Each is worth understanding before you spend a dollar. Select up to three to compare
                    their planning questions, infrastructure needs and approval pathways side by side.
                  </p>
                  <div className="mt-6 space-y-4" data-testid="explorer-opportunities">
                    {result.opportunities.map((o) => (
                      <OpportunityCard
                        key={o.key}
                        o={o}
                        inCompare={!!compare.find((x) => x.key === o.key)}
                        compareFull={compare.length >= 3}
                        onToggleCompare={toggleCompare}
                      />
                    ))}
                  </div>
                </div>
              </FadeIn>

              <FadeIn>
                <div className="bg-white border border-cloud p-7" data-testid="explorer-considerations">
                  <Eyebrow>Key planning considerations</Eyebrow>
                  <ul className="mt-4 space-y-2.5">
                    {result.key_considerations.map((t, i) => (
                      <li key={i} className="flex gap-3 text-sm text-charcoal/80 leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sand shrink-0" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>

              <FadeIn>
                <div className="bg-white border border-cloud p-7" data-testid="explorer-next-steps">
                  <Eyebrow>What to investigate next</Eyebrow>
                  <ol className="mt-4 space-y-2.5">
                    {result.next_steps.map((s, i) => (
                      <li key={i} className="flex gap-3 text-sm text-charcoal/80 leading-relaxed">
                        <span className="font-mono text-teal shrink-0">{i + 1}.</span>{s}
                      </li>
                    ))}
                  </ol>
                </div>
              </FadeIn>

              <FadeIn>
                <div data-testid="explorer-resources">
                  <Eyebrow>Where to find the information</Eyebrow>
                  <div className="mt-5 grid sm:grid-cols-2 gap-4">
                    {result.resources.map((r) => (
                      <a
                        key={r.key}
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`resource-${r.key}`}
                        className="group bg-white border border-cloud p-5 hover:border-teal transition-colors"
                      >
                        <p className="font-medium text-sm text-teal inline-flex items-center gap-1.5">
                          {r.title} <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </p>
                        <p className="mt-1.5 text-xs text-charcoal/70 leading-relaxed">{r.note}</p>
                        <p className="mt-2.5 text-[10px] font-mono uppercase tracking-[0.12em] text-charcoal/45">
                          Official source · link checked {r.checked}
                        </p>
                      </a>
                    ))}
                  </div>
                </div>
              </FadeIn>

              <FadeIn>
                <div className="bg-white border border-cloud p-7" data-testid="explorer-council-questions">
                  <Eyebrow>Questions to ask council or a planner</Eyebrow>
                  <ul className="mt-4 space-y-2.5">
                    {result.council_questions.map((t, i) => (
                      <li key={i} className="flex gap-3 text-sm text-charcoal/80 leading-relaxed">
                        <HelpCircle className="w-4 h-4 text-teal shrink-0 mt-0.5" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeIn>

              {result.unknowns?.length > 0 && (
                <FadeIn>
                  <div className="bg-sand-light/50 border border-cloud p-7" data-testid="explorer-unknowns">
                    <Eyebrow>What we can't tell you yet</Eyebrow>
                    <p className="mt-3 text-sm text-charcoal/70 max-w-2xl leading-relaxed">
                      Being upfront about the unknowns is part of planning well. Here's what we couldn't
                      establish, why it matters, and exactly how to find out.
                    </p>
                    <div className="mt-5 space-y-4">
                      {result.unknowns.map((u, i) => {
                        const res = result.resources.find((r) => r.key === u.resource_key);
                        return (
                          <div key={i} className="bg-white border border-cloud p-5">
                            <p className="font-medium text-sm text-teal">{u.what}</p>
                            <p className="mt-1.5 text-xs text-charcoal/70 leading-relaxed"><span className="font-mono uppercase tracking-[0.1em] text-charcoal/50">Why it matters — </span>{u.why}</p>
                            <p className="mt-1.5 text-xs text-charcoal/70 leading-relaxed"><span className="font-mono uppercase tracking-[0.1em] text-charcoal/50">How to find out — </span>{u.how}</p>
                            {res && (
                              <a href={res.url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs text-teal underline underline-offset-4">
                                {res.title} <ArrowUpRight className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </FadeIn>
              )}

              <FadeIn>
                <ToolkitCta />
              </FadeIn>

              <FadeIn>
                <div className="bg-sand-light/60 border border-cloud p-8" data-testid="explorer-planner-connect">
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal">Coming soon — personalised planning support</p>
                  <h3 className="mt-3 font-serif text-xl lg:text-2xl text-teal">Once you've explored, the next step gets easier</h3>
                  <p className="mt-3 text-sm text-charcoal/75 leading-relaxed max-w-2xl">
                    Once you've explored your opportunities and completed your Plan First workbook,
                    the next step will be even easier. We're developing a service that will help
                    connect you with a suitably qualified planning consultant who understands your
                    location, your development goals and the questions you've already identified.
                  </p>
                  <p className="mt-3 text-sm text-charcoal/75 leading-relaxed max-w-2xl">
                    From understanding your land to exploring its potential, Plan First is here to
                    help you move forward with confidence.
                  </p>
                  <Link to="/book" className="mt-5 inline-flex items-center gap-2 border border-teal text-teal text-sm font-medium px-6 py-3 rounded-full hover:bg-teal hover:text-ivory transition-colors" data-testid="explorer-planner-connect-cta">
                    Planner Connect — learn more <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </FadeIn>

              <div className="flex flex-wrap items-center gap-4">
                <button
                  onClick={restart}
                  data-testid="explorer-restart"
                  className="inline-flex items-center gap-2 border border-cloud text-charcoal/70 text-sm px-5 py-2.5 rounded-full hover:border-teal hover:text-teal transition-colors"
                >
                  <RotateCcw className="w-4 h-4" /> Explore another property
                </button>
                <p className="text-xs text-charcoal/55 leading-relaxed max-w-xl">
                  Educational information only — not planning, legal or financial advice. NSW planning
                  rules change; verify current requirements before acting. Resources last checked {RURAL_REVIEW_DATE}.{" "}
                  <Link to="/legal/disclaimer" className="text-teal underline underline-offset-2">Full disclaimer</Link>
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {compare.length > 0 && phase === "ready" && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-teal text-ivory shadow-2xl" data-testid="compare-tray">
          <div className="max-w-4xl mx-auto px-4 py-3.5 flex items-center justify-between gap-4">
            <p className="text-sm">
              <GitCompareArrows className="w-4 h-4 inline mr-2 -mt-0.5" />
              {compare.length} opportunit{compare.length === 1 ? "y" : "ies"} selected
              <span className="hidden sm:inline text-ivory/60"> — {compare.map((o) => o.title.split(" &")[0]).join(" · ")}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCompareOpen(true)}
                disabled={compare.length < 2}
                data-testid="compare-open"
                className="bg-ivory text-teal text-xs font-medium px-5 py-2.5 rounded-full hover:bg-sand transition-colors disabled:opacity-50"
              >
                Compare side by side
              </button>
              <button onClick={() => setCompare([])} className="p-2 text-ivory/70 hover:text-ivory" data-testid="compare-clear">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {compareOpen && compare.length >= 2 && (
        <CompareTable
          items={compare}
          onRemove={(k) => setCompare((prev) => prev.filter((x) => x.key !== k))}
          onClose={() => setCompareOpen(false)}
        />
      )}

      <Info className="hidden" />
    </div>
  );
};

export default RuralExplorer;
